import { ElementRef, Injectable, NgZone, OnDestroy } from '@angular/core';
import { NumberTypes, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, NotificationService } from '@gh/core-ui';
import { isNotNil, setHours, setMinutes } from '@gh/core-util';
import { debounceTime$, delay$, distinctUntilChanged$, filter$, map$ } from '@gh/rx/operators';
import { GridApi, IRowNode } from 'ag-grid-community';
import { find, first, isEmpty, isNil, map, reverse, uniq } from 'lodash';
import {
  GanttState,
  GanttStateEventType,
  queryGanttAllParentTaskIds,
  queryGanttAllTasks,
  queryGanttAncestorTaskIds,
  queryGanttDescendantTaskIds,
  queryGanttDestinationTaskByNumberForBubble,
  queryGanttDestinationTaskByNumberForMove,
  queryGanttEndDate,
  queryGanttFocusedCell,
  queryGanttHasInvalidTask,
  queryGanttIsDateValid,
  queryGanttIsDragAllowed,
  queryGanttLastChildIndexByNumber,
  queryGanttLeftTrimToPredecessors,
  queryGanttLeftTrimToWorkTime,
  queryGanttMultiMode,
  queryGanttNextTaskNumber,
  queryGanttRedoDisabled,
  queryGanttRightTrimToPredecessors,
  queryGanttRightTrimToWorkTime,
  queryGanttStartDate,
  queryGanttStartTask,
  queryGanttTask,
  queryGanttTaskIndex,
  queryGanttTaskPath,
  queryGanttTasksByParent,
  queryGanttTotalDuration,
  queryGanttUndoDisabled,
  queryGanttUnconfirmedTaskCount,
  queryGanttVisibleTaskSeq,
  queryTaskCompletionPct,
  setTaskFields,
} from '../gantt-chart.state';
import {
  completionPctFromTimelineTaskProgress,
  completionPctToTimelineTaskProgress,
  GanttChartBounds,
  GanttTask,
  GanttTaskRelation,
  ganttTaskRelationFromTaskDto,
  ganttTaskRelationFromTimelineLink,
  ganttTaskRelationToTimelineLink,
  ganttTaskToTimelineTask,
  getGanttTaskId,
  HOURS_END_OF_DAY,
  HOURS_START_OF_DAY,
  isGanttTaskIncluded,
  normalizeTaskRelations,
  validateGanttTaskField,
} from '../resources/gantt.model';
import { GanttChartBusService } from './gantt-chart-bus.service';
import { parsePredecessors, ScheduleType, stringifyPredecessors } from '@gh/builder-shared-data';
import { ScheduleGanttDetails } from '../../builder-scheduling/resources/schedule-gantt-details.model';
import {
  ScheduleTemplateGanttDetails,
} from '../../builder-setup/scheduling/resources/schedule-template-gantt-details.model';
import { Observable, Subscription } from 'rxjs';
import {
  GanttEditTaskDialogConfig,
  ganttEditTaskDialogRoute,
} from '../components/gantt-edit-task-dialog/gantt-edit-task-dialog.component';

@Injectable()
export class GanttChartCommandService implements OnDestroy {
  isUndoDisabled$ = this.state.select(queryGanttUndoDisabled);
  isRedoDisabled$ = this.state.select(queryGanttRedoDisabled);
  private api: GridApi;
  private treeFilterRaf = 0;
  private searchDebounceTimer: any;
  private searchDisplayRaf = 0;
  private searchIdleHandle: number | null = null;
  private pendingSearchString: string | null = null;
  private static readonly SEARCH_MIN_LEN = 3;
  private static readonly SEARCH_DEBOUNCE_MS = 120;
  private static readonly SEARCH_CLEAR_DEBOUNCE_MS = 16;
  private cellRefreshRaf = 0;
  private pendingCellRefresh = new Map<string, Set<string>>();
  private scrollPreserveDepth = 0;
  private preservedGridScrollTop: number | null = null;
  private historyGate: (() => void) | null = null;
  private scrollCleanup: (() => void) | null = null;
  private timelineScrollSubscription: Subscription | null = null;

  constructor(
    private state: State<GanttState>,
    private bus: GanttChartBusService,
    private dialog: DialogService,
    private notifications: NotificationService,
    private elementRef: ElementRef,
    private ngZone: NgZone,
  ) {
    // Here we read actual state of tasks from DHTMLX component and update tasks in state correspondingly
    // delay(0) is important here because DHTMLX component does not propagate changes immediately to descendants
    // when onAfterAutoSchedule event is called
    bus.afterAutoSchedule.pipe(delay$(0)).subscribe((tasks) => {
      // DHTMLX component notifies about auto scheduling of tasks only, but not ancestors and descendants.
      // Here we take all ancestor and descendant tasks from DHTMLX component
      // and update these tasks in state
      const ancestors = this.state.project(queryGanttAncestorTaskIds, tasks.map((task) => task.id))
                            .map((taskId) => bus.getTask(taskId));
      const descendants = this.state.project(queryGanttDescendantTaskIds, tasks.map((task) => task.id))
                              .map((taskId) => bus.getTask(taskId));
      const tasksWithRelatives = ancestors
      .concat(descendants)
      .concat(tasks)
      // exclude tasks which are not included into gantt chart (excluded tasks)
      .filter(Boolean);

      this.state.mutate({
                          type : GanttStateEventType.TaskUpdateFromTimeline,
                          tasks: tasksWithRelatives,
                        });
      this.syncTopTasksForMultiMode();
      this.refreshTaskCells(
        tasksWithRelatives.map((task) => task.id),
        ['startDate', 'endDate', 'duration', 'donePct', 'completionPct'],
      );
    });
    bus.afterLinkDelete.subscribe((link) => {
      if (!this.bus.allowAfterLinkSync) {
        return;
      }
      // when link was added on the gantt component we have to add this link into the model
      const task = this.state.project(queryGanttTask, link.target);
      // do we have this link in model already?
      if (isNotNil(find(task.predecessors, { taskId: link.source }))) {
        this.state.mutate({
                            type  : GanttStateEventType.TaskSetField,
                            taskId: link.target,
                            field : 'predecessors',
                            value : task.predecessors ? task.predecessors.filter(
                              (predecessor) => predecessor.id !== link.id) : [],
                          });
        if (task.parentTaskId) {
          this.recalculateAncestors(task.parentTaskId);
        }
        this.refreshTaskCells([link.target], ['predecessors']);
      }
      this.syncTopTasksForMultiMode();
    });
    bus.afterLinkAdd.subscribe((link) => {
      if (!this.bus.allowAfterLinkSync) {
        return;
      }
      // when link was added on the gantt component we have to add this link into the model
      const task = this.state.project(queryGanttTask, link.target);
      // do we have this link in model already?
      if (isNil(find(task.predecessors, { taskId: link.source }))) {
        this.state.mutate({
                            type  : GanttStateEventType.TaskSetField,
                            taskId: link.target,
                            field : 'predecessors',
                            value : (task.predecessors || []).concat(
                              [ganttTaskRelationFromTimelineLink(link, this.state.project(queryGanttAllTasks))]),
                          });

        if (task.parentTaskId) {
          this.recalculateAncestors(task.parentTaskId);
        }
        this.refreshTaskCells([link.target], ['predecessors']);
      }
      this.syncTopTasksForMultiMode();
    });

    bus.afterTaskUpdate.subscribe((timelineTask) => {
      const task = this.state.project(queryGanttTask, timelineTask.id);
      // here we track changing of progress on timeline and propagate this change to parent task
      if (timelineTask.progress !== completionPctToTimelineTaskProgress(task.completionPct || 0)) {
        this.setTaskCompletionPct(timelineTask.id, completionPctFromTimelineTaskProgress(timelineTask.progress));
      }
    });
  }

  registerHistoryGate(gate: (() => void) | null): void {
    this.historyGate = gate;
  }

  redo(): void {
    this.historyGate?.();
    this.state.mutate({
                        type: GanttStateEventType.Redo,
                      });
    this.syncAfterHistoryStep();
  }

  undo(): void {
    this.historyGate?.();
    this.state.mutate({
                        type: GanttStateEventType.Undo,
                      });
    this.syncAfterHistoryStep();
  }

  private syncAfterHistoryStep(): void {
    this.syncTreeExpandedState();
    this.refreshTreeFilter();
    requestAnimationFrame(() => {
      const tasks = this.getAllTasks();
      this.bus.applyTaskProgressBatch(
        tasks.map((task) => ({
          taskId: task.taskId,
          progress: completionPctToTimelineTaskProgress(task.completionPct || 0),
        })),
      );
    });
  }

  private buildTaskProgressUpdates(
    updates: Array<{ taskId: ID; fields: Hash<any> }>,
  ): Array<{ taskId: ID; progress: number }> {
    return updates.map(({ taskId, fields }) => ({
      taskId,
      progress: completionPctToTimelineTaskProgress(fields.completionPct),
    }));
  }

  initHistory(): void {
    this.state.mutate({
                        type: GanttStateEventType.InitHistory,
                      });
  }

  addToHistory(): void {
    this.state.mutate({
                        type: GanttStateEventType.AddToHistory,
                      });
  }

  getNextTaskNumber(): number {
    return this.state.project(queryGanttNextTaskNumber);
  }

  getTaskIndex(taskId: ID): number {
    return this.state.project(queryGanttTaskIndex, taskId);
  }

  getStartTask(): Maybe<GanttTask> {
    return this.state.project(queryGanttStartTask);
  }

  selectTask(taskId: ID): void {
    const previousSelectedId = this.getSelectedTaskId();
    if (previousSelectedId === taskId) {
      return;
    }
    if (this.hasActiveColumnSort()) {
      this.state.mutate({
        type: GanttStateEventType.SelectTask,
        taskId,
      });
      if (previousSelectedId) {
        this.state.mutate({
          type: GanttStateEventType.UnselectTask,
          taskId: previousSelectedId,
        });
      }
    } else if (this.bus.gantt) {
      this.ngZone.runOutsideAngular(() => {
        this.bus.gantt.selectTask(taskId);
      });
    }
    this.syncSelectionInGrid(taskId, previousSelectedId);
  }

  private getSelectedTaskId(): ID | undefined {
    for (const taskUniqId in this.state.value.tasks) {
      if (this.state.value.tasks[taskUniqId]?.selected) {
        return taskUniqId;
      }
    }
    return;
  }

  private syncSelectionInGrid(taskId: ID, previousSelectedId?: ID): void {
    if (!this.api) {
      return;
    }
    const rowNodes: IRowNode[] = [];
    uniq([taskId, previousSelectedId].filter(Boolean) as ID[]).forEach((id) => {
      const task = this.state.project(queryGanttTask, id);
      const node = this.api!.getRowNode(String(id));
      if (node && task) {
        node.setData(task);
        rowNodes.push(node);
      }
    });
    if (rowNodes.length) {
      this.api.refreshCells({ rowNodes, force: true });
    }
  }

  getSelectedTask(): Maybe<GanttTask> {
    const selectedId = this.bus.gantt.getSelectedId();
    if (selectedId) {
      return this.getTask(selectedId);
    }
    return;
  }

  getTask(taskId: ID): GanttTask {
    return this.state.project(queryGanttTask, taskId);
  }

  isIncludedTaskExist(taskId: ID): boolean {
    const task = this.state.project(queryGanttTask, taskId);
    return isNotNil(task) && isGanttTaskIncluded(task) || false;
  }

  isRelationsAllowed(taskId: ID, relations: GanttTaskRelation[]): boolean {
    return isEmpty(this.bus.validateLinks(
      taskId,
      relations.map((relation) => ganttTaskRelationToTimelineLink(taskId, relation)),
    ));
  }

  validateRelations(taskId: ID, relations: GanttTaskRelation[]): GanttTaskRelation[] {
    const result = this.bus.validateLinks(
      taskId,
      relations.map((relation) => ganttTaskRelationToTimelineLink(taskId, relation)),
    );
    return result.length ? result.filter((link) => !!link && link.id).map(
      ({ id }) => <GanttTaskRelation>find(relations, { id })) : [];
  }

  correctTaskStartDate(date: Date): Date {
    return this.bus.correctTaskStartDate(date);
  }

  showCompletedTasks(show: boolean): void {
    this.state.mutate({
                        type: GanttStateEventType.SetShowCompletedTasks,
                        show,
                      });

    this.bus.showCompletedTasks(show);
  }

  showDeletedTasks(show: boolean): void {
    this.state.mutate({
                        type: GanttStateEventType.SetShowDeletedTasks,
                        show,
                      });

    this.bus.showDeletedTasks(show);
  }

  addTasks(tasks: GanttTask[]): void {
    const hasParentTask = tasks.some((task) => isNotNil(task.parentTaskId));
    if (hasParentTask) {
      throw new Error('GanttChartCommandService: addTasks allows to add only top level tasks');
    }

    const destinationIndexInRoot = this.state.project(queryGanttLastChildIndexByNumber);
    this.state.mutate({
                        type            : GanttStateEventType.TaskAddSet,
                        tasks,
                        destinationOrder: destinationIndexInRoot,
                      });

    requestAnimationFrame(() => {
      this.bus.batchUpdate(() => {
        this.bus.addTasks(tasks.map(ganttTaskToTimelineTask), destinationIndexInRoot);
      });
      this.bus.batchUpdate(() => {
        this.recalculateAncestors(first(tasks)!.parentTaskId!);
      });
      this.syncTopTasksForMultiMode();
    });
  }

  addTask(task: GanttTask, branchIndex?: number): void {
    const destinationIndexInParent = isNotNil(branchIndex)
      ? branchIndex
      : this.state.project(queryGanttLastChildIndexByNumber, task.parentTaskId);
    this.state.mutate({
                        type            : GanttStateEventType.TaskAdd,
                        task,
                        destination     : task.parentTaskId,
                        destinationOrder: destinationIndexInParent,
                      });
    this.refreshTreeFilter();

    requestAnimationFrame(() => {
      this.bus.batchUpdate(() => {
        this.bus.addTask(ganttTaskToTimelineTask(task), destinationIndexInParent);
      });
      this.bus.batchUpdate(() => {
        if (task.parentTaskId) {
          this.recalculateAncestors(task.parentTaskId);
          this.bus.openTask(task.parentTaskId);
        }
      });
      this.syncTopTasksForMultiMode();
    });
  }

  removeTask(taskId: ID): void {
    const task = this.state.project(queryGanttTask, taskId);

    this.state.mutate({
                        type: GanttStateEventType.TaskRemove,
                        taskId,
                      });

    requestAnimationFrame(() => {
      this.bus.batchUpdate(() => {
        this.bus.deleteTask(taskId);
      });
      this.bus.batchUpdate(() => {
        if (task.parentTaskId) {
          this.recalculateAncestors(task.parentTaskId);
        }
      });
      this.syncTopTasksForMultiMode();
      this.recalculateStartEndDates();
    });
  }

  unmarkAsDeletedTask(taskId: ID): void {
    this.state.mutate({
                        type: GanttStateEventType.UnMarkAsDeleted,
                        taskId,
                      });
    this.bus.batchUpdate(() => {
      this.bus.unMarkAsDeletedTask(taskId);
    });
    this.syncTopTasksForMultiMode();
    this.recalculateStartEndDates();
    requestAnimationFrame(() => this.reDrawRows(taskId));
  }

  markAsDeletedTask(taskId: ID): void {
    const task = this.state.project(queryGanttTask, taskId);
    const parentTaskId = task.parentTaskId;
    if (parentTaskId) {
      const getRootParent = (id: string): any => {
        const item = this.state.project(queryGanttTask, id);
        if (item.parentTaskId) {
          return getRootParent(item.parentTaskId);
        }
        return id;
      };
      const rootParent = getRootParent(parentTaskId);
      const destination =  this.state.project(queryGanttDestinationTaskByNumberForBubble, this.state.value.tasksByParent[rootParent][0]);
      this.state.mutate({
                          type            : GanttStateEventType.TaskMove,
                          source          : taskId,
                          destination     : undefined,
                          destinationOrder: destination?.taskOrder || 1,
                        });
    }
    this.state.mutate({
                        type: GanttStateEventType.MarkAsDeleted,
                        taskId,
                      });

    requestAnimationFrame(() => {
      this.bus.batchUpdate(() => {
        this.bus.markAsDeletedTask(taskId);
      });
      this.bus.batchUpdate(() => {
        if (parentTaskId) {
          this.recalculateAncestors(parentTaskId);
        }
      });
      this.syncTopTasksForMultiMode();
      this.recalculateStartEndDates();
    });
  }

  setTaskField(taskId: ID, field: string, value: any): void {
    switch (field) {
      case 'name':
        this.setTaskName(taskId, value);
        break;
      case 'noteMemo':
        this.setTaskNote(taskId, value);
        break;
      case 'resource':
        this.setTaskResource(taskId, value);
        break;
      case 'predecessors':
        this.setTaskPredecessors(taskId, value);
        break;
      case 'duration':
        this.setTaskDuration(taskId, value);
        break;
      case 'completionPct':
        this.setTaskCompletionPct(taskId, value);
        break;
      case 'startDate':
        this.setTaskStartDate(taskId, value);
        break;
      case 'endDate':
        this.setTaskEndDate(taskId, value);
        break;
      case 'description':
        this.setTaskDescription(taskId, value);
        break;
      case 'confirmedWith':
        this.setTaskConfirmedWith(taskId, value);
        break;
      case 'confirmedOnDate':
        this.setTaskConfirmedOnDate(taskId, value);
        break;
      case 'confirmedForDate':
        this.setTaskConfirmedForDate(taskId, value);
        break;
      case 'isExcludeFromGanttChart':
        this.setTaskExcluded(taskId, value);
        break;
      default:
        break;
    }
    this.syncTopTasksForMultiMode();
  }

  isInvalid(): boolean {
    return this.state.project(queryGanttHasInvalidTask);
  }

  navigateToInvalid(): void {
    this.state.mutate({ type: GanttStateEventType.NavigateToInvalid });
    const focusedCell = this.state.project(queryGanttFocusedCell);
    if (focusedCell?.id) {
      this.showTaskInTimeline(focusedCell.id);
    }
  }

  resetFocusedCell(): void {
    this.state.mutate({ type: GanttStateEventType.ResetFocusedCell });
  }

  moveTask(sourceTaskId: ID, destinationTaskId: Maybe<ID>, destinationTaskOrder: number): void {
    const sourceTask = this.state.project(queryGanttTask, sourceTaskId);
    this.state.mutate({
                        type            : GanttStateEventType.TaskMove,
                        source          : sourceTaskId,
                        destination     : destinationTaskId,
                        destinationOrder: destinationTaskOrder,
                      });

    requestAnimationFrame(() => {
      this.bus.batchUpdate(() => {
        if (sourceTask.parentTaskId !== destinationTaskId) {
          if (sourceTask.parentTaskId) {
            this.recalculateAncestors(sourceTask.parentTaskId, true);
          }
          if (destinationTaskId) {
            this.recalculateAncestors(destinationTaskId);
          }
        }
        this.bus.moveTask(sourceTaskId, destinationTaskOrder, sourceTask.parentTaskId, destinationTaskId);
      });
      this.syncTopTasksForMultiMode();
    });
  }

  dragTask(sourceTask: any, destinationTask?: any): void {
    const sourceTaskId = getGanttTaskId(sourceTask);
    const destinationTaskId = destinationTask ? getGanttTaskId(destinationTask) : undefined;
    if (!this.state.project(queryGanttIsDragAllowed, sourceTaskId, destinationTaskId)) {
      return;
    }

    const { taskId, taskOrder } = this.state.project(
      queryGanttDestinationTaskByNumberForMove,
      sourceTaskId,
      destinationTaskId,
    );

    // if (this.state.project(queryGanttIsTaskHaveCommonLinks, sourceTaskId, destinationTaskId)) {
    //   this.notifications.error('msg_sch_cyclic_dependence');
    //   return;
    // }

    this.moveTask(sourceTaskId, taskId, taskOrder);

    const relations = parsePredecessors(stringifyPredecessors(sourceTask.predecessors));

    if (isNil(relations)) {
      return;
    }

    const isRelationsAllowed = this.isRelationsAllowed(
      sourceTaskId,
      relations.map((rel) => ganttTaskRelationFromTaskDto(sourceTask, rel)),
    );
    if (!isRelationsAllowed) {
      // const { taskId, taskOrder } = this.state.project(
      //   queryGanttDestinationTaskByNumberForMove,
      //   destinationTaskId,
      //   sourceTaskId);
      if (taskId) {
        this.moveTask(taskId, sourceTaskId, taskOrder);
        this.notifications.error('msg_sch_cyclic_dependence');
      }
      return;
    }
  }

  bubbleTask(taskIdToBubble: ID): void {
    const destination = this.state.project(queryGanttDestinationTaskByNumberForBubble, taskIdToBubble);
    if (destination) {
      const { taskId, taskOrder } = destination;
      this.moveTask(taskIdToBubble, taskId, taskOrder);
      this.refreshTreeFilter();
    }
  }

  setTaskOpened(taskId: ID, opened: boolean): void {
    this.state.mutate({ type: GanttStateEventType.TaskOpen, taskId, opened });
    const updatedTask = this.state.project(queryGanttTask, taskId);
    const rowNode = this.api?.getRowNode(String(taskId));
    if (rowNode && updatedTask) {
      rowNode.setData(updatedTask);
    }
    this.refreshTreeFilter();
    requestAnimationFrame(() => {
      if (opened) {
        this.bus.openTask(taskId);
      } else {
        this.bus.closeTask(taskId);
      }
    });
  }

  expandAllNotCompleted(): void {
    this.openAllNotCompleted();
  }

  expandAll(): void {
    this.openAll(true);
  }

  collapseAll(): void {
    this.openAll(false);
  }

  getAllTasks(): GanttTask[] {
    return this.state.project(queryGanttAllTasks);
  }

  getAllTasks$(): Observable<GanttTask[]> {
    return this.state.select(queryGanttAllTasks);
  }

  getUnconfirmedTaskCount$(): Observable<number> {
    return this.state.select(queryGanttUnconfirmedTaskCount).pipe(
      distinctUntilChanged$(),
    );
  }

  resetDirty(): void {
    this.state.mutate({ type: GanttStateEventType.ResetDirty });
  }

  showTaskInTimeline(taskId: ID): void {
    if (this.hasActiveColumnSort()) {
      return;
    }
    const ancestorsToOpen = this.getCollapsedAncestorIds(taskId);
    if (ancestorsToOpen.length) {
      this.state.mutate({
        type: GanttStateEventType.TaskOpenAll,
        taskIds: ancestorsToOpen,
        opened: true,
      });
      this.refreshTreeFilterNow();
      ancestorsToOpen.forEach((id) => {
        const task = this.state.project(queryGanttTask, id);
        const rowNode = this.api?.getRowNode(String(id));
        if (rowNode && task) {
          rowNode.setData(task);
        }
      });
      this.ngZone.runOutsideAngular(() => {
        this.bus.gantt?.batchUpdate(() => {
          ancestorsToOpen.forEach((id) => this.bus.gantt.open(id));
        });
      });
    }
    this.navigateToTaskView(taskId);
  }

  /**
   * Lightweight reveal for grid row clicks: scrolls only the timeline to the task bar.
   * Unlike {@link showTaskInTimeline} it does not scroll the grid, expand ancestors or mutate state,
   * so it is safe to call while a cell is being edited.
   */
  scrollTimelineToTask(taskId: ID): void {
    if (!this.bus.gantt || this.hasActiveColumnSort()) {
      return;
    }
    requestAnimationFrame(() => {
      if (this.bus.gantt?.isTaskExists(taskId)) {
        this.bus.showTask(taskId);
      }
    });
  }

  private refreshTreeFilterNow(): void {
    this.bus.invalidateTreeVisibilityCache();
    if (this.bus.searchString) {
      this.bus.rebuildSearchVisibilityCache();
    }
    this.bus.rebuildTreeVisibilityCache();
    this.api?.onFilterChanged();
  }

  private getCollapsedAncestorIds(taskId: ID): ID[] {
    const path = this.state.project(queryGanttTaskPath, taskId);
    return path.slice(0, -1).filter((id) => {
      const task = this.state.project(queryGanttTask, id);
      return !!task?.hasChildren && !task.opened;
    });
  }

  private navigateToTaskView(taskId: ID): void {
    this.bus.suppressScrollSync = true;
    try {
      this.scrollGridToTask(taskId);
      this.revealTimelineTask(taskId);
      this.selectTask(taskId);
    } finally {
      requestAnimationFrame(() => {
        this.bus.suppressScrollSync = false;
      });
    }
  }

  private revealTimelineTask(taskId: ID): void {
    this.ngZone.runOutsideAngular(() => {
      if (!this.bus.gantt?.getTask(taskId)) {
        return;
      }
      this.bus.gantt.showTask(taskId);
    });
  }

  private scrollGridToTask(taskId: ID): void {
    if (!this.api) {
      return;
    }
    const rowNode = this.api.getRowNode(String(taskId));
    const rowIndex = rowNode?.rowIndex ?? this.getVisibleGridRowIndex(taskId);
    if (rowIndex == null || rowIndex < 0) {
      return;
    }
    this.api.ensureIndexVisible(rowIndex, 'middle');
    const top = this.getGridViewportEl()?.scrollTop ?? 0;
    this.bus.scrollVerticallyToSync(top);
  }

  private getVisibleGridRowIndex(taskId: ID): number {
    return this.state.project(queryGanttVisibleTaskSeq)
      .filter((task) => this.bus.isTaskVisibleInTree(task.taskId)
        && (!this.bus.searchString || this.bus.isTaskVisibleInSearch(task.taskId)))
      .findIndex((task) => task.taskId === taskId);
  }

  recalculateStartEndDates(): void {
    const tasks = this.state.project(queryGanttAllTasks);
    let startDate: any = undefined;
    let endDate: any = undefined;
    tasks.forEach((task) => {
      if (!task.isExcludeFromGanttChart && !task.markedAsDeleted) {
        if (!startDate || startDate > task.startDate) {
          startDate = task.startDate;
        }
        if (!endDate || endDate < task.endDate) {
          endDate = task.endDate;
        }
      }
    });
    this.state.mutate({
                        type: GanttStateEventType.RecalculateStartEndDate,
                        startDate,
                        endDate,
                      });
  }

  calculateBounds(): GanttChartBounds {
    return {
      startDate: this.state.project(queryGanttStartDate),
      endDate  : this.state.project(queryGanttEndDate),
      duration : this.state.project(queryGanttTotalDuration),
    };
  }

  _setGridApi(api: GridApi): void {
    this.api = api;

    this.setupScrollEvents();
  }

  ngOnDestroy(): void {
    if (this.treeFilterRaf) {
      cancelAnimationFrame(this.treeFilterRaf);
      this.treeFilterRaf = 0;
    }
    if (this.cellRefreshRaf) {
      cancelAnimationFrame(this.cellRefreshRaf);
      this.cellRefreshRaf = 0;
    }
    if (this.searchDisplayRaf) {
      cancelAnimationFrame(this.searchDisplayRaf);
      this.searchDisplayRaf = 0;
    }
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    if (this.searchIdleHandle != null) {
      const idleWindow = window as Window & { cancelIdleCallback?: (id: number) => void };
      if (typeof idleWindow.cancelIdleCallback === 'function') {
        idleWindow.cancelIdleCallback(this.searchIdleHandle);
      }
      this.searchIdleHandle = null;
    }
    this.timelineScrollSubscription?.unsubscribe();
    this.timelineScrollSubscription = null;
    this.scrollCleanup?.();
    this.scrollCleanup = null;
    this.pendingCellRefresh.clear();
  }

  private static readonly BASE_DATE_COLUMNS = ['baseStartDate', 'baseEndDate'];
  private static readonly EXCLUDE_REFRESH_COLUMNS = [
    'isExcludeFromGanttChart',
    'predecessors',
    'resource',
    'donePct',
    'completionPct',
    'duration',
    'startDate',
    'endDate',
  ];

  setBaseDateColumnsVisible(show: boolean, allowedColumns: string[] = []): void {
    if (!this.api) {
      return;
    }
    const existing = GanttChartCommandService.BASE_DATE_COLUMNS.filter(
      (colId) => !!this.api!.getColumn(colId),
    );
    if (!existing.length) {
      return;
    }
    this.withPreservedGridScroll(() => {
      if (!show) {
        this.api!.setColumnsVisible(existing, false);
        return;
      }
      const toShow = existing.filter((colId) => allowedColumns.includes(colId));
      const toHide = existing.filter((colId) => !allowedColumns.includes(colId));
      if (toHide.length) {
        this.api!.setColumnsVisible(toHide, false);
      }
      if (toShow.length) {
        this.api!.setColumnsVisible(toShow, true);
      }
    });
  }

  private hasActiveColumnSort(): boolean {
    return this.api.getColumnState().some((c) => c.sort != null);
  }

  saveBaseDates(): void {
    this.state.mutate({ type: GanttStateEventType.SaveBaseDates });
  }

  loadTasks(schedule: ScheduleGanttDetails | ScheduleTemplateGanttDetails): void {
    this.state.mutate({
                        type : GanttStateEventType.LoadTasks,
                        tasks: schedule.tasks,
                      });
  }

  reDrawRows(taskId?: ID): void {
    this.withPreservedGridScroll(() => {
      if (taskId) {
        const node = this.api.getRowNode(String(taskId));
        if (node) {
          this.api.redrawRows({ rowNodes: [node] });
        }
      } else {
        this.api.redrawRows();
      }
    });
  }

  openEditTaskDialog(taskId: string, scheduleType?: ScheduleType, isTemplate?: boolean, isCombined?: boolean): void {
    const targetTask = this.state.project(queryGanttTask, taskId);
    this.dialog.openWithInput(ganttEditTaskDialogRoute, <GanttEditTaskDialogConfig>{
      task: targetTask,
      scheduleType,
      gantt: this.bus.gantt,
      state: this.state,
      isTemplate,
      isCombined,
    }).afterClosed().pipe(
      filter$(Boolean))
        .subscribe((updatedTask: GanttTask | 'delete') => {
          if (updatedTask === 'delete') {
            if (targetTask.canBeMarkedAsDeleted) {
              this.markAsDeletedTask(taskId);
            } else {
              this.removeTask(taskId);
            }
            return;
          }
          this.state.mutate(
            {
              type: GanttStateEventType.EditTaskDialog,
              task: { ...targetTask, ...updatedTask },
            });
          if (targetTask.parentTaskId) {
            this.recalculateAncestors(targetTask.parentTaskId);
          }
          this.bus.setTaskEditDialog(taskId, updatedTask);
          requestAnimationFrame(() => {
            const n = this.api.getRowNode(String(taskId));
            const updated = this.state.project(queryGanttTask, taskId);
            if (n && updated) {
              n.setData(updated);
              this.api.redrawRows({ rowNodes: [n] });
            }
          });
        });
  }

  private setTaskName(taskId: ID, name: string): void {
    const task = this.getTask(taskId);
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'name',
                        value: name,
                      });
    this.bus.setTaskName(taskId, name, task);
    this.refreshTaskCells([taskId], ['name']);
  }

  private setTaskNote(taskId: ID, value: any): void {
    this.state.mutate({
                        type  : GanttStateEventType.TaskSetFieldsWithoutDirty,
                        taskId,
                        fields: {
                          noteMemo  : value.memo,
                          recentNote: value,
                        },
                      });
    this.bus.setTaskNote(taskId, value);
    this.refreshTaskCells([taskId], ['notes']);
  }

  private setTaskResource(taskId: ID, resource: any): void {
    const task = this.getTask(taskId);
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'resource',
                        value: resource,
                      });
    this.bus.setTaskResource(taskId, resource, task);
    this.refreshTaskCells([taskId], ['resource']);
  }

  private setTaskDescription(taskId: ID, description: string): void {
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'description',
                        value: description,
                      });
    this.bus.setTaskDescription(taskId, description);
    this.refreshTaskCells([taskId], ['description']);
  }

  private setTaskConfirmedWith(taskId: ID, confirmedWith: string): void {
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'confirmedWith',
                        value: confirmedWith,
                      });
    this.bus.setTaskConfirmedWith(taskId, confirmedWith);
    this.refreshTaskCells([taskId], ['confirmedWith']);
  }

  private setTaskConfirmedOnDate(taskId: ID, date: Date): void {
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'confirmedOnDate',
                        value: date,
                      });
    this.bus.setTaskConfirmedOnDate(taskId, date);
    this.refreshTaskCells([taskId], ['confirmedOnDate']);
  }

  private setTaskConfirmedForDate(taskId: ID, date: Date): void {
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'confirmedForDate',
                        value: date,
                      });
    this.bus.setTaskConfirmedOnDate(taskId, date);
    this.refreshTaskCells([taskId], ['confirmedForDate']);
  }

  private setTaskExcluded(taskId: ID, excluded: boolean): void {
    const task = this.getTask(taskId);
    const idList = [taskId];
    const linkIds: string[] = [];
    const getParents = (parentId: ID, list: string[] = []): any => {
      list.push(parentId);
      const parentTask = this.getTask(parentId);
      if (parentTask.parentTaskId) {
        return getParents(parentTask.parentTaskId, list);
      }
      return list;
    };
    const parentsList: ID[] = task.parentTaskId ? getParents(task.parentTaskId) : [];
    const taskByParent = this.state.project(queryGanttTasksByParent);
    const idSet = new Set<ID>(idList);

    const setChildren = (id: ID) => {
      const children = taskByParent[id];
      if (children) {
        children.forEach((child) => {
          if (!idSet.has(child)) {
            idSet.add(child);
            idList.push(child);
            if (taskByParent[child]) {
              setChildren(child);
            }
          }
        });
      }
    };
    setChildren(taskId);
    const excludedSubtreeIds = new Set<ID>(idList);
    const predecessorDependentIds: ID[] = [];
    if (excluded) {
      this.getAllTasks().forEach((item) => {
        if (item.predecessors?.some((predecessor) => excludedSubtreeIds.has(predecessor.taskId))) {
          predecessorDependentIds.push(item.taskId);
        }
      });
    }
    if (excluded) {
      const affectedIds = new Set<ID>([...idList, ...parentsList]);
      const linkIdSet = new Set<string>();
      for (const item of this.getAllTasks()) {
        if (affectedIds.has(item.taskId)) {
          item.predecessors?.forEach((predecessor) => linkIdSet.add(predecessor.id));
        }
        item.predecessors?.forEach((predecessor) => {
          if (affectedIds.has(predecessor.taskId)) {
            linkIdSet.add(predecessor.id);
          }
        });
      }
      linkIds.push(...linkIdSet);
    }
    this.state.mutate({
                        type       : GanttStateEventType.TasksSetExcl,
                        task,
                        idList,
                        parentsList: parentsList || [],
                        value      : excluded,
                      });
    if (excluded && linkIds.length) {
      this.bus.removeLinks(linkIds);
    }
    idList.forEach((id: string) => {
      this.bus.setTaskExcluded(id, excluded);
    });
    if (taskByParent[task.taskId] && excluded) {
      this.state.mutate({
        type: GanttStateEventType.TaskSetField,
        taskId: task.taskId,
        field: 'completionPct',
        value: 0,
      });
    }

    const ancestorRootId = taskByParent[task.taskId] && !excluded
      ? task.taskId
      : task.parentTaskId;
    if (ancestorRootId) {
      this.recalculateAncestors(ancestorRootId, true);
    }
    this.recalculateStartEndDates();
    this.refreshTaskCells(
      [...idList, ...parentsList, ...predecessorDependentIds],
      GanttChartCommandService.EXCLUDE_REFRESH_COLUMNS,
    );
  }

  private setTaskDuration(taskId: ID, duration: number): void {
    if (isNil(duration) || isNotNil(NumberTypes.HoursNotZero.validate(duration))) {
      return;
    }
    this.bus.setTaskDuration(taskId, duration);
  }

  private setTaskCompletionPct(taskId: ID, completionPct: number): void {
    const currentTask = this.state.project(queryGanttTask, taskId);
    if ((currentTask.completionPct || 0) === (completionPct || 0)) {
      return;
    }

    const workingState = setTaskFields(this.state.getValue(), taskId, { completionPct });
    const updates: Array<{ taskId: ID; fields: Hash<any> }> = [{
      taskId,
      fields: { completionPct },
    }];

    const updatedTask = workingState.tasks[taskId];
    if (updatedTask?.parentTaskId) {
      updates.push(...this.buildAncestorFieldUpdates(updatedTask.parentTaskId, false, workingState));
    }

    this.state.mutate({
      type: GanttStateEventType.TaskSetFieldsBatch,
      updates,
    });

    this.refreshTaskCells(updates.map(({ taskId: id }) => id), ['donePct', 'completionPct']);
    requestAnimationFrame(() => {
      this.bus.applyTaskProgressBatch(this.buildTaskProgressUpdates(updates));
    });
  }

  private setTaskPredecessors(taskId: ID, predecessors: GanttTaskRelation[]): void {
    const task = this.state.project(queryGanttTask, taskId);
    const normalizedPredecessors = normalizeTaskRelations(predecessors, task.predecessors || []);
    this.state.mutate({
                        type : GanttStateEventType.TaskSetField,
                        taskId,
                        field: 'predecessors',
                        value: normalizedPredecessors,
                      });

    // Here is an intentional separation of two batch updates.
    // The first operation can lead to recalculation of task/project dates
    // and this recalculation is deferred until batch is completed.
    this.bus.batchUpdate(() => {
      this.bus.replaceLinks(
        map(task.predecessors, (predecessor) => ganttTaskRelationToTimelineLink(taskId, predecessor)),
        map(normalizedPredecessors, (predecessor) => ganttTaskRelationToTimelineLink(taskId, predecessor)),
      );
    });
    this.bus.batchUpdate(() => {
      if (task.parentTaskId) {
        this.recalculateAncestors(task.parentTaskId);
      }
    });
    this.refreshTaskCells([taskId], ['predecessors']);
  }

  private setTaskStartDate(taskId: ID, date: Date): void {
    if (!date || !this.state.project(queryGanttIsDateValid, date)) {
      return;
    }
    const task = this.state.project(queryGanttTask, taskId);
    const roundDate = setMinutes(setHours(date, HOURS_START_OF_DAY), 0);
    // trim date by task predecessors
    const trimmedToPredecessorsDate = this.state.project(queryGanttLeftTrimToPredecessors, taskId, roundDate);
    // trim date to worktime
    const trimmedToWorkTimeDate = this.state.project(queryGanttLeftTrimToWorkTime, trimmedToPredecessorsDate);
    if (isNotNil(validateGanttTaskField(task, 'startDate', trimmedToWorkTimeDate))) {
      return;
    }
    this.bus.setTaskStartDate(taskId, trimmedToWorkTimeDate);
  }

  private setTaskEndDate(taskId: ID, date: Date): void {
    if (!this.state.project(queryGanttIsDateValid, date)) {
      return;
    }
    const task = this.state.project(queryGanttTask, taskId);
    const roundDate = setMinutes(setHours(date, HOURS_END_OF_DAY), 0);
    // trim date by task predecessors
    const trimmedToPredecessorsDate = this.state.project(queryGanttRightTrimToPredecessors, taskId, roundDate);
    const trimmedToWorkTimeDate = this.state.project(queryGanttRightTrimToWorkTime, trimmedToPredecessorsDate);
    if (isNotNil(validateGanttTaskField(task, 'endDate', trimmedToWorkTimeDate))) {
      return;
    }
    this.bus.setTaskEndDate(taskId, trimmedToWorkTimeDate);
  }

  private openAllNotCompleted(): void {
    const parentTaskIds = this.state.project(queryGanttAllParentTaskIds);
    const idsToExpand: string[] = [];
    parentTaskIds.forEach((id) => {
      const task = this.getTask(id);
      if (!task.completionPct || task.completionPct < 100) {
        idsToExpand.push(id);
      }
    });
    this.state.mutate({ type: GanttStateEventType.TaskOpenAll, taskIds: idsToExpand, opened: true });
    this.refreshTreeFilter();
    requestAnimationFrame(() => {
      this.syncOpenedIconsInGrid(idsToExpand);
      this.bus.openTasks(idsToExpand);
    });
  }

  private openAll(opened: boolean): void {
    const parentTaskIds = this.state.project(queryGanttAllParentTaskIds);

    this.state.mutate({ type: GanttStateEventType.TaskOpenAll, taskIds: parentTaskIds, opened });
    this.refreshTreeFilter();
    requestAnimationFrame(() => {
      this.syncOpenedIconsInGrid(parentTaskIds);
      if (opened) {
        this.bus.openTasks(parentTaskIds);
      } else {
        this.bus.closeTasks(parentTaskIds);
      }
    });
  }

  private syncOpenedIconsInGrid(taskIds: ID[]): void {
    if (!this.api || isEmpty(taskIds)) {
      return;
    }
    const rowNodes: IRowNode[] = [];
    taskIds.forEach((taskId) => {
      const task = this.state.project(queryGanttTask, taskId);
      const rowNode = this.api!.getRowNode(String(taskId));
      if (!rowNode || !task) {
        return;
      }
      rowNode.setData(task);
      rowNodes.push(rowNode);
    });
    if (rowNodes.length) {
      this.api.refreshCells({
        rowNodes,
        columns: ['taskNumber'],
        force: true,
      });
    }
  }

  applySearchFilter(rawSearch: string): void {
    const trimmed = (rawSearch || '').trim();
    const isClearing = trimmed.length < GanttChartCommandService.SEARCH_MIN_LEN;
    this.pendingSearchString = isClearing ? '' : trimmed;
    this.cancelPendingSearchApply();

    const debounceMs = isClearing
      ? GanttChartCommandService.SEARCH_CLEAR_DEBOUNCE_MS
      : GanttChartCommandService.SEARCH_DEBOUNCE_MS;

    this.searchDebounceTimer = setTimeout(() => {
      this.searchDebounceTimer = null;
      this.commitSearchFilter(isClearing);
    }, debounceMs);
  }

  private cancelPendingSearchApply(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    if (this.searchDisplayRaf) {
      cancelAnimationFrame(this.searchDisplayRaf);
      this.searchDisplayRaf = 0;
    }
    if (this.searchIdleHandle != null) {
      const idleWindow = window as Window & { cancelIdleCallback?: (id: number) => void };
      if (typeof idleWindow.cancelIdleCallback === 'function') {
        idleWindow.cancelIdleCallback(this.searchIdleHandle);
      }
      this.searchIdleHandle = null;
    }
  }

  private commitSearchFilter(isClearing: boolean): void {
    const nextSearch = this.pendingSearchString ?? '';
    this.pendingSearchString = null;
    if (nextSearch === (this.bus.searchString || '')) {
      return;
    }
    this.bus.setFilter(nextSearch);
    this.scheduleSearchDisplayUpdate(isClearing);
  }

  private scheduleSearchDisplayUpdate(isClearing: boolean): void {
    const runDisplay = () => {
      this.searchDisplayRaf = 0;
      this.searchIdleHandle = null;
      this.ngZone.runOutsideAngular(() => {
        if (!this.api) {
          return;
        }
        this.withPreservedGridScroll(() => {
          this.api!.onFilterChanged();
        });
        if (this.bus.initialized) {
          this.bus.refreshSearchDisplay();
        }
      });
    };

    const idleWindow = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const idleTimeout = isClearing ? 200 : 120;
    if (typeof idleWindow.requestIdleCallback === 'function') {
      this.searchIdleHandle = idleWindow.requestIdleCallback(runDisplay, { timeout: idleTimeout });
      return;
    }

    this.searchDisplayRaf = requestAnimationFrame(runDisplay);
  }

  refreshTreeFilter(): void {
    this.bus.invalidateTreeVisibilityCache();
    if (this.bus.searchString) {
      this.bus.rebuildSearchVisibilityCache();
    }
    this.bus.rebuildTreeVisibilityCache();
    if (this.treeFilterRaf) {
      return;
    }
    this.treeFilterRaf = requestAnimationFrame(() => {
      this.treeFilterRaf = 0;
      this.withPreservedGridScroll(() => {
        this.api?.onFilterChanged();
      });
    });
  }

  private recalculateAncestors(parentTaskId: ID, resetCompletionIfNoChildren: boolean = false): void {
    const updates = this.buildAncestorFieldUpdates(parentTaskId, resetCompletionIfNoChildren);
    if (isEmpty(updates)) {
      return;
    }

    this.state.mutate({
      type: GanttStateEventType.TaskSetFieldsBatch,
      updates,
    });

    this.refreshTaskCells(
      updates.map(({ taskId: id }) => id),
      ['donePct', 'completionPct'],
    );
    requestAnimationFrame(() => {
      this.bus.applyTaskProgressBatch(this.buildTaskProgressUpdates(updates));
    });
  }

  private buildAncestorFieldUpdates(
    parentTaskId: ID,
    resetCompletionIfNoChildren: boolean = false,
    baseState: GanttState = this.state.getValue(),
  ): Array<{ taskId: ID; fields: Hash<any> }> {
    const taskPath = queryGanttTaskPath(baseState, parentTaskId);
    if (isEmpty(taskPath)) {
      return [];
    }

    const updates: Array<{ taskId: ID; fields: Hash<any> }> = [];
    let workingState = baseState;

    reverse(taskPath).forEach((nextTaskId, i) => {
      const timelineTask = this.bus.getTask(nextTaskId);
      const nextCompletionPct = queryTaskCompletionPct(
        workingState,
        nextTaskId,
        resetCompletionIfNoChildren && i === 0,
      );
      const fields = {
        completionPct: nextCompletionPct,
        duration: timelineTask.duration,
        startDate: timelineTask.start_date,
        endDate: timelineTask.end_date,
      };
      updates.push({ taskId: nextTaskId, fields });
      workingState = setTaskFields(workingState, nextTaskId, fields);
    });

    return updates;
  }

  private refreshTaskCells(taskIds: ID[], columns: string[]): void {
    if (!this.api || isEmpty(taskIds)) {
      return;
    }
    taskIds.forEach((id) => {
      const key = String(id);
      let cols = this.pendingCellRefresh.get(key);
      if (!cols) {
        cols = new Set<string>();
        this.pendingCellRefresh.set(key, cols);
      }
      columns.forEach((column) => cols!.add(column));
    });
    if (this.cellRefreshRaf) {
      return;
    }
    this.cellRefreshRaf = requestAnimationFrame(() => {
      this.cellRefreshRaf = 0;
      if (!this.api) {
        this.pendingCellRefresh.clear();
        return;
      }
      this.withPreservedGridScroll(() => {
        const columnSet = new Set<string>();
        const rowNodes: IRowNode[] = [];
        this.pendingCellRefresh.forEach((cols, taskId) => {
          cols.forEach((column) => columnSet.add(column));
          const task = this.state.project(queryGanttTask, taskId);
          const node = this.api!.getRowNode(String(taskId));
          if (node && task) {
            node.setData(task);
            rowNodes.push(node);
          }
        });
        this.pendingCellRefresh.clear();
        if (rowNodes.length) {
          this.api!.refreshCells({
            rowNodes,
            columns: [...columnSet],
            force: columnSet.has('donePct') || columnSet.has('isExcludeFromGanttChart'),
          });
        }
      });
    });
  }

  private getGridViewportEl(): HTMLElement | null {
    return this.elementRef.nativeElement.querySelector('.ag-body-viewport') as HTMLElement | null;
  }

  private withPreservedGridScroll(op: () => void): void {
    const viewport = this.getGridViewportEl();
    if (!viewport) {
      op();
      return;
    }
    if (this.scrollPreserveDepth === 0) {
      this.preservedGridScrollTop = viewport.scrollTop;
      this.bus.suppressScrollSync = true;
    }
    this.scrollPreserveDepth += 1;
    try {
      op();
    } finally {
      this.scrollPreserveDepth -= 1;
      if (this.scrollPreserveDepth === 0) {
        const top = this.preservedGridScrollTop ?? 0;
        this.preservedGridScrollTop = null;
        const releaseScrollSync = () => {
          this.bus.suppressScrollSync = false;
        };
        if (Math.abs(viewport.scrollTop - top) > 0.5) {
          viewport.scrollTop = top;
          requestAnimationFrame(() => {
            if (viewport && Math.abs(viewport.scrollTop - top) > 0.5) {
              viewport.scrollTop = top;
            }
            releaseScrollSync();
          });
        } else {
          releaseScrollSync();
        }
      }
    }
  }

  private syncTreeExpandedState(): void {
    const parentTasks = this.getAllTasks().filter((task) => task.hasChildren);
    const toOpen = parentTasks.filter((task) => task.opened).map((task) => task.taskId);
    const toClose = parentTasks.filter((task) => !task.opened).map((task) => task.taskId);
    if (toClose.length) {
      this.bus.closeTasks(toClose);
    }
    if (toOpen.length) {
      this.bus.openTasks(toOpen);
    }
  }

  private syncTopTasksForMultiMode(): void {
    // in multi mode duration of the topmost task should be calculated in another way
    if (this.state.project(queryGanttMultiMode)) {
      this.state.mutate({ type: GanttStateEventType.TaskSyncTopsForMultiMode });
    }
  }

  private setupScrollEvents(): void {
    if (this.scrollCleanup) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      const gridViewportEl = this.elementRef.nativeElement.querySelector('.ag-body-viewport') as HTMLElement;
      if (!gridViewportEl) {
        return;
      }

      const rowHeight = 35;
      const snapToRow = (top: number) => Math.round(top / rowHeight) * rowHeight;

      let lastSyncedTop = snapToRow(gridViewportEl.scrollTop);
      let syncingFromTimeline = false;
      let gridScrolling = false;
      let lastUserGridTop = lastSyncedTop;
      let scrollRaf = 0;
      let settleTimer: ReturnType<typeof setTimeout> | null = null;

      const syncGridTopToTimeline = () => {
        scrollRaf = 0;
        if (this.bus.suppressScrollSync || syncingFromTimeline) {
          return;
        }
        const snapped = snapToRow(gridViewportEl.scrollTop);
        lastUserGridTop = gridViewportEl.scrollTop;
        if (Math.abs(snapped - lastSyncedTop) < 1) {
          return;
        }
        lastSyncedTop = snapped;
        this.bus.scrollVerticallyToSync(snapped);
      };

      const onGridScroll = () => {
        if (this.bus.suppressScrollSync || syncingFromTimeline) {
          return;
        }
        gridScrolling = true;
        if (!scrollRaf) {
          scrollRaf = requestAnimationFrame(syncGridTopToTimeline);
        }
        if (settleTimer) {
          clearTimeout(settleTimer);
        }
        settleTimer = setTimeout(() => {
          settleTimer = null;
          gridScrolling = false;
          syncGridTopToTimeline();
        }, 64);
      };

      gridViewportEl.addEventListener('scroll', onGridScroll, { passive: true });

      // Timeline → grid: never follow while editing/scrolling; reject jump-to-top.
      this.timelineScrollSubscription = this.bus.scroll.pipe(
        filter$(({ deltaY }) => deltaY !== 0),
        map$(({ top }) => snapToRow(top)),
        distinctUntilChanged$(),
        filter$((top) => !this.bus.suppressScrollSync
          && !gridScrolling
          && Math.abs(top - lastUserGridTop) >= rowHeight
          && !(top < rowHeight && lastUserGridTop > rowHeight * 2)),
        debounceTime$(64),
      ).subscribe((top) => {
        lastSyncedTop = top;
        syncingFromTimeline = true;
        this.bus.suppressScrollSync = true;
        gridViewportEl.scrollTop = top;
        lastUserGridTop = top;
        requestAnimationFrame(() => {
          syncingFromTimeline = false;
          this.bus.suppressScrollSync = false;
        });
      });

      this.scrollCleanup = () => {
        gridViewportEl.removeEventListener('scroll', onGridScroll);
        if (scrollRaf) {
          cancelAnimationFrame(scrollRaf);
          scrollRaf = 0;
        }
        if (settleTimer) {
          clearTimeout(settleTimer);
          settleTimer = null;
        }
      };
    });
  }
}
